<script lang="ts">
  import { Button } from "@/components/ui/button"
  import { DialogFooter, DialogHeader } from "@/components/ui/dialog"
  import { cn } from "@/utils"

  interface Props {
    open?: boolean
    title?: string
    description: string
    confirmLabel?: string
    cancelLabel?: string
    variant?: "default" | "destructive"
    onConfirm: () => void
    onCancel?: () => void
  }

  let {
    open = $bindable(false),
    title = "Confirm",
    description,
    confirmLabel = "Confirm",
    cancelLabel = "Cancel",
    variant = "default",
    onConfirm,
    onCancel,
  }: Props = $props()

  let dialogRef: HTMLDialogElement | null = $state(null)
  let submitButtonRef: HTMLButtonElement | null = $state(null)

  $effect(() => {
    if (dialogRef && open) {
      dialogRef.showModal()
      setTimeout(() => {
        if (submitButtonRef) {
          submitButtonRef.focus()
        }
      }, 0)
    }
  })

  function handleKeyDown(event: KeyboardEvent) {
    if (event.key === "Enter" && !event.defaultPrevented) {
      event.preventDefault()
      handleConfirm()
    } else if (event.key === "Escape") {
      event.preventDefault()
      handleCancel()
    }
  }

  function handleConfirm() {
    onConfirm()
    open = false
  }

  function handleCancel() {
    if (onCancel) {
      onCancel()
    }
    open = false
  }

  function handleClose() {
    open = false
  }
</script>

{#if open}
  <dialog
    bind:this={dialogRef}
    onclose={handleClose}
    onkeydown={handleKeyDown}
    class={cn(
      "bg-background fixed top-[50%] left-[50%] z-50 grid w-full max-w-[calc(100%-2rem)] translate-x-[-50%] translate-y-[-50%] gap-4 rounded-lg border p-6 shadow-lg duration-200 sm:max-w-lg",
      "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
    )}
  >
    <form
      method="dialog"
      onsubmit={(e) => {
        e.preventDefault()
        handleConfirm()
      }}
    >
      <DialogHeader>
        <h2 class="text-lg leading-none font-semibold">{title}</h2>
        <p class="text-muted-foreground text-sm">{description}</p>
      </DialogHeader>
      <DialogFooter>
        <Button type="button" onclick={handleCancel} variant="outline"
          >{cancelLabel}</Button
        >
        <Button bind:ref={submitButtonRef} type="submit" {variant}
          >{confirmLabel}</Button
        >
      </DialogFooter>
    </form>
  </dialog>
{/if}

<style>
  dialog::backdrop {
    background-color: rgb(0 0 0 / 0.5);
    backdrop-filter: blur(4px);
  }
</style>
